
import { GoogleGenAI, Type, Schema } from "@google/genai";
import { auth } from './firebase';
import { IdeaDetails, InvestorPitch } from '../types';

// Environment Configuration
// Safely access environment variables to handle various runtime environments (Vite, Cloudflare, AI Studio)
const getEnvVar = (key: string): string | undefined => {
    try {
        // @ts-ignore
        if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
            // @ts-ignore
            return import.meta.env[key];
        }
    } catch (e) {}

    try {
        // Check for process.env (Vite define replacement or Node environment)
        if (typeof process !== 'undefined' && process.env) {
            // @ts-ignore
            if (process.env[key]) return process.env[key];
        }
    } catch (e) {}

    return undefined;
};

// Use safe accessors or direct replacement targets
const PROXY_SERVER_URL = getEnvVar('VITE_AI_PROXY_SERVER');

// Access API_KEY with multiple fallback strategies
const API_KEY = 
    (typeof process !== 'undefined' && process.env && process.env.API_KEY) ? process.env.API_KEY :
    getEnvVar('API_KEY') || 
    getEnvVar('VITE_GEMINI_API_KEY');

// Constants for Models
export const MODEL_FLASH = 'gemini-2.5-flash';
export const MODEL_PRO = 'gemini-3-pro-preview';
export const MODEL_IMAGE_PRO = 'gemini-3-pro-image-preview'; // Only for Pro/Business

// Direct SDK Instance (Fallback)
const getAiFallback = () => {
  if (!API_KEY) throw new Error("API Key not found. Please check your environment variables.");
  return new GoogleGenAI({ apiKey: API_KEY });
};

// --- Helper: Call AI (Proxy or Direct) ---

const callGeminiAPI = async (
  model: string, 
  contents: any, 
  config: any
): Promise<{ text: string; raw: any }> => {
  
  const user = auth.currentUser;

  // 1. USE PROXY if Configured + User Logged In
  if (PROXY_SERVER_URL && user) {
    const token = await user.getIdToken();
    
    // Normalize 'contents' to array format if string
    const normalizedContents = typeof contents === 'string' 
      ? [{ role: 'user', parts: [{ text: contents }] }] 
      : contents;

    const response = await fetch(`${PROXY_SERVER_URL}/api/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-app-name': 'idea-shelf'
      },
      body: JSON.stringify({
        model,
        contents: normalizedContents,
        config: config 
      })
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`AI Proxy Error: ${response.status} ${errText}`);
    }

    const data = await response.json();
    
    // Shim the response to match SDK behavior (add .text getter)
    return {
      text: data.candidates?.[0]?.content?.parts?.[0]?.text || "",
      raw: data
    };
  } 
  
  // 2. FALLBACK: Direct SDK (Guests or No Proxy)
  else {
    const ai = getAiFallback();
    const response = await ai.models.generateContent({
      model,
      contents,
      config
    });
    
    // SDK response object already has .text accessor
    return {
      text: response.text || "",
      raw: response
    };
  }
};

// --- Shared Schemas ---

const adCopySchema: Schema = {
  type: Type.ARRAY,
  items: {
      type: Type.OBJECT,
      properties: {
          platform: { type: Type.STRING, description: "e.g. Facebook, LinkedIn, Google Search" },
          headline: { type: Type.STRING, description: "Punchy, click-worthy headline." },
          body: { type: Type.STRING, description: "Persuasive copy focusing on value/pain." }
      },
      required: ["platform", "headline", "body"]
  }
};

const growthStrategySchema: Schema = {
  type: Type.OBJECT,
  properties: {
      acquisition: { type: Type.STRING, description: "AARRR: Channels to get users (SEO, Ads, Virality)." },
      activation: { type: Type.STRING, description: "AARRR: The 'Happy First Experience'." },
      retention: { type: Type.STRING, description: "AARRR: What brings them back?" },
      referral: { type: Type.STRING, description: "AARRR: Viral loops and incentives." },
      revenue: { type: Type.STRING, description: "AARRR: Monetization tactics." }
  },
  required: ["acquisition", "activation", "retention", "referral", "revenue"]
};

const keyMomentsSchema: Schema = {
  type: Type.OBJECT,
  properties: {
     setupMoment: { type: Type.STRING, description: "Friction users must overcome to start." },
     ahaMoment: { type: Type.STRING, description: "The precise moment value is realized." },
     habitMoment: { type: Type.STRING, description: "The hook that forms a daily/weekly habit." }
  },
  required: ["setupMoment", "ahaMoment", "habitMoment"]
};

// --- Modular Prompt Registry ---

interface PromptModule {
    system: string;
    schema: Schema;
}

export const PROMPT_MODULES: Record<string, PromptModule> = {
    core: {
        system: `You are a world-class market researcher. Analyze the problem space deeply. 
        - Estimate market size (TAM/SAM/SOM) with logic and Fermi estimation.
        - Identify 3-5 REAL or highly probable competitors.
        - Define a sharp Unique Value Proposition (UVP) that attacks the weakness of incumbents.
        - Simulate user verbatims/quotes based on typical complaints in this sector (Reddit/G2 style).
        Format lists with bullet points.`,
        schema: {
            type: Type.OBJECT,
            properties: {
                problemToSolve: { type: Type.STRING, description: "Deep analysis of the pain point. Include simulated user quotes." },
                marketEstimate: { type: Type.STRING, description: "TAM/SAM/SOM analysis with logic." },
                totalMarketSizeMM: { type: Type.NUMBER, description: "Numeric Total Addressable Market in Millions (USD). e.g., 500." },
                competitors: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Specific competitors with their Achilles heel." },
                marketSizeScore: { type: Type.NUMBER, description: "1-10 score of market attractiveness." },
                uniqueValueProposition: { type: Type.STRING, description: "The single most compelling reason to buy." },
                targetCustomers: { type: Type.STRING, description: "Specific personas and customer segments." },
                customerRelationships: { type: Type.STRING, description: "How you interact (Self-service, Dedicated support, etc.)." },
            },
            required: ["problemToSolve", "marketEstimate", "totalMarketSizeMM", "competitors", "marketSizeScore", "uniqueValueProposition", "targetCustomers", "customerRelationships"]
        }
    },
    product: {
        system: `You are a product strategist. 
        - Define the Product Form clearly (SaaS, App, etc.).
        - Describe the 'Aha!' moment.
        - Define a 'Concierge MVP' or 'Wizard of Oz' strategy to validate without code.
        Format lists with bullet points.`,
        schema: {
            type: Type.OBJECT,
            properties: {
                productForm: { type: Type.STRING, description: "Form factor: Mobile App, SaaS, Marketplace, Hardware, etc." },
                potentialSolution: { type: Type.STRING, description: "High-level solution description." },
                howItWorks: { type: Type.STRING, description: "Step-by-step functional walkthrough." },
                userExperience: { type: Type.STRING, description: "Vibe, emotion, and design language." },
                mvpValidation: { type: Type.STRING, description: "Non-scalable strategy to validate 0-1 (Waitlist, Concierge MVP, etc.)." },
                keyActivities: { type: Type.ARRAY, items: { type: Type.STRING }, description: "What the business actually DOES." },
                keyResources: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Assets required (IP, Capital, Human)." },
            },
            required: ["productForm", "potentialSolution", "howItWorks", "userExperience", "mvpValidation", "keyActivities", "keyResources"]
        }
    },
    growth: {
        system: `You are a growth hacker. 
        - Define the Growth Strategy using AARRR. 
        - Write high-conversion Landing Page copy (H1, Subhead, CTA).
        - Create 3 distinct Ad variations.
        IMPORTANT: Use plain text formatting. Use new lines for structure. Do NOT use Markdown symbols.`,
        schema: {
            type: Type.OBJECT,
            properties: {
                growthStrategy: growthStrategySchema,
                keyMoments: keyMomentsSchema,
                goToMarketStrategy: { type: Type.STRING, description: "Launch plan for the first 90 days. Plain text." },
                marketingUserJourney: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Steps from 'Unaware' to 'loyal advocate'." },
                landingPageCopy: { type: Type.STRING, description: "Structure: Hero H1, Subheadline, CTA, and Social Proof text. Plain text only." },
                adCopies: adCopySchema,
            },
            required: ["growthStrategy", "keyMoments", "goToMarketStrategy", "marketingUserJourney", "landingPageCopy", "adCopies"]
        }
    },
    business: {
        system: `You are a CFO and Business Architect. 
        - Define the Revenue Streams (Pricing tiers, unit economics).
        - Analyze Cost Structure (Fixed vs Variable).
        - Map out the Service Blueprint.
        Format lists with bullet points.`,
        schema: {
            type: Type.OBJECT,
            properties: {
                revenueStreams: { type: Type.STRING, description: "Pricing model (Subscription, Transactional, Freemium)." },
                costStructure: { type: Type.STRING, description: "Major cost drivers (Servers, CAC, Salaries)." },
                detailedCostAnalysis: { type: Type.STRING, description: "Deep dive into unit economics." },
                serviceDesign: { type: Type.STRING, description: "Behind-the-scenes processes to deliver the value." },
                keyPartners: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Suppliers, Partners, Integrations." },
                channels: { type: Type.ARRAY, items: { type: Type.STRING }, description: "Distribution channels (App Store, Direct Sales)." },
                timeToMarketMonths: { type: Type.NUMBER, description: "Estimated months to launch MVP." },
            },
            required: ["revenueStreams", "costStructure", "detailedCostAnalysis", "serviceDesign", "keyPartners", "channels", "timeToMarketMonths"]
        }
    }
};

// --- Granular Field Definitions & Dependency Graph ---

interface SmartFieldDefinition {
    system: string;
    contextKeys: (keyof IdeaDetails)[];
    schema: Schema;
}

export const FIELD_DEFINITIONS: Record<string, SmartFieldDefinition> = {
    // Market
    problemToSolve: {
        system: "Analyze the core problem deeply. Use your broad knowledge base to simulate real user complaints/quotes.",
        contextKeys: [],
        schema: { type: Type.STRING }
    },
    marketEstimate: {
        system: "Estimate TAM/SAM/SOM using Fermi estimation and market reports. Be specific with numbers.",
        contextKeys: ['problemToSolve', 'targetCustomers'],
        schema: { type: Type.STRING }
    },
    competitors: {
        system: "Identify 3-5 direct competitors and their weaknesses.",
        contextKeys: ['problemToSolve', 'potentialSolution'],
        schema: { type: Type.ARRAY, items: { type: Type.STRING } }
    },
    uniqueValueProposition: {
        system: "Synthesize a sharp UVP that differentiates from competitors.",
        contextKeys: ['problemToSolve', 'competitors', 'potentialSolution'],
        schema: { type: Type.STRING }
    },
    targetCustomers: {
        system: "Define specific user personas and segments.",
        contextKeys: ['problemToSolve'],
        schema: { type: Type.STRING }
    },
    
    // Product
    potentialSolution: {
        system: "Describe the high-level solution concept.",
        contextKeys: ['problemToSolve'],
        schema: { type: Type.STRING }
    },
    mvpValidation: {
        system: "Propose a low-fidelity 0-1 validation strategy (Concierge, Waitlist, Smoke Test).",
        contextKeys: ['potentialSolution', 'targetCustomers'],
        schema: { type: Type.STRING }
    },
    howItWorks: {
        system: "Step-by-step user flow description.",
        contextKeys: ['potentialSolution'],
        schema: { type: Type.STRING }
    },
    
    // Growth
    landingPageCopy: {
        system: "Write high-converting landing page copy (H1, Subhead, CTA). Plain text, no markdown.",
        contextKeys: ['uniqueValueProposition', 'targetCustomers', 'potentialSolution'],
        schema: { type: Type.STRING }
    },
    marketingUserJourney: {
        system: "Map the user journey from awareness to advocacy.",
        contextKeys: ['targetCustomers', 'potentialSolution'],
        schema: { type: Type.ARRAY, items: { type: Type.STRING } }
    },
    goToMarketStrategy: {
        system: "Define a launch plan for the first 90 days. Plain text.",
        contextKeys: ['targetCustomers', 'channels'],
        schema: { type: Type.STRING }
    },

    // Business
    revenueStreams: {
        system: "Define the pricing model and revenue mechanics.",
        contextKeys: ['productForm', 'targetCustomers', 'competitors'],
        schema: { type: Type.STRING }
    },
    costStructure: {
        system: "Identify major fixed and variable costs.",
        contextKeys: ['keyActivities', 'keyResources'],
        schema: { type: Type.STRING }
    },
    keyPartners: {
        system: "Identify strategic partners and suppliers.",
        contextKeys: ['keyActivities', 'channels'],
        schema: { type: Type.ARRAY, items: { type: Type.STRING } }
    },

    // Pitch Deck (Critical Fix: Schema Definition for Regeneration)
    investorPitch: {
        system: `You are a legendary VC pitch deck consultant. Regenerate the pitch deck content.
        Structure:
        1. Company Purpose: "AirBnB for X"
        2. Problem
        3. Solution
        4. Why Now
        5. Market Potential
        6. Competition
        7. Product Overview
        8. Business Model
        9. Team
        10. Financials
        11. Vision
        12. Call to Action
        
        Return a JSON object with these keys.`,
        contextKeys: ['problemToSolve', 'potentialSolution', 'targetCustomers', 'revenueStreams', 'competitors'],
        schema: {
            type: Type.OBJECT,
            properties: {
                companyPurpose: { type: Type.STRING },
                problem: { type: Type.STRING },
                solution: { type: Type.STRING },
                whyNow: { type: Type.STRING },
                marketPotential: { type: Type.STRING },
                competition: { type: Type.STRING },
                productOverview: { type: Type.STRING },
                businessModel: { type: Type.STRING },
                team: { type: Type.STRING },
                financials: { type: Type.STRING },
                vision: { type: Type.STRING },
                callToAction: { type: Type.STRING }
            },
            required: ["companyPurpose", "problem", "solution", "whyNow", "marketPotential", "competition", "productOverview", "businessModel", "team", "financials", "vision", "callToAction"]
        }
    }
};

// --- Generation Functions ---

export const generateSection = async (title: string, description: string, moduleKey: keyof typeof PROMPT_MODULES, model: string = MODEL_FLASH): Promise<Partial<IdeaDetails>> => {
    const module = PROMPT_MODULES[moduleKey];
    if (!module) throw new Error(`Unknown module: ${moduleKey}`);

    const userPrompt = `Analyze this product idea:
    Title: ${title}
    Description: ${description}`;

    const response = await callGeminiAPI(
        model,
        userPrompt,
        {
            systemInstruction: module.system,
            responseMimeType: "application/json",
            responseSchema: module.schema
        }
    );

    return JSON.parse(response.text);
};

// Orchestrator: Runs all modules to generate the full idea
export const expandIdea = async (title: string, description: string, model: string = MODEL_FLASH): Promise<IdeaDetails> => {
    // Run in parallel for speed
    const [core, product, growth, business] = await Promise.all([
        generateSection(title, description, 'core', model),
        generateSection(title, description, 'product', model),
        generateSection(title, description, 'growth', model),
        generateSection(title, description, 'business', model)
    ]);

    return { ...core, ...product, ...growth, ...business } as IdeaDetails;
};

// New Granular Regeneration with Dependency Injection
export const regenerateSmartField = async (
    idea: { title: string, description: string, details?: IdeaDetails },
    fieldKey: string,
    instructions?: string,
    model: string = MODEL_FLASH
): Promise<any> => {
    
    // 1. Resolve Definition
    const definition = FIELD_DEFINITIONS[fieldKey];
    
    // Fallback if not in granular definitions, try to find in modules or default to generic string
    let schema: Schema = { type: Type.STRING };
    let system = "You are a product expert. Regenerate this field.";
    let contextData: any = {};

    if (definition) {
        schema = definition.schema;
        system = definition.system;
        
        // 2. Build Dependency Context
        if (idea.details) {
            definition.contextKeys.forEach(key => {
                if (idea.details![key]) {
                    contextData[key] = idea.details![key];
                }
            });
        }
    } else {
        // Fallback search in modules
        for (const mod of Object.values(PROMPT_MODULES)) {
            if (mod.schema.properties && mod.schema.properties[fieldKey]) {
                schema = mod.schema.properties[fieldKey] as Schema;
                break;
            }
        }
    }

    // 3. Construct Prompt
    const prompt = `
    Task: Regenerate the field "${fieldKey}" for the idea "${idea.title}".
    
    Idea Description: ${idea.description}
    
    Context Information:
    ${Object.entries(contextData).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join('\n')}
    
    Current Value: ${idea.details ? JSON.stringify((idea.details as any)[fieldKey]) : 'None'}
    
    User Instructions: ${instructions || "Improve clarity and depth."}
    
    Return ONLY the valid JSON value for this field.
    `;

    const response = await callGeminiAPI(
        model,
        prompt,
        {
            systemInstruction: system,
            responseMimeType: "application/json",
            responseSchema: schema
        }
    );

    return JSON.parse(response.text);
};

// Legacy / Single Field Regeneration (Maintained for EditModal compatibility)
export const regenerateIdeaField = async (
    title: string,
    description: string,
    fieldKey: string,
    instructions: string,
    currentValue: any,
    model: string = MODEL_FLASH
): Promise<any> => {
    return regenerateSmartField({ title, description, details: { [fieldKey]: currentValue } as any }, fieldKey, instructions, model);
};

// Pitch Deck Generator
export const generatePitchDeck = async (title: string, description: string, details: IdeaDetails, model: string = MODEL_FLASH): Promise<InvestorPitch> => {
    const userPrompt = `Create a strict, Sequoia-style investor pitch deck for:
    Title: ${title}
    Description: ${description}
    
    Use this research context to populate the slides: 
    ${JSON.stringify(details)}
    
    CRITICAL: You MUST generate content for ALL 12 slides defined in the schema.
    Think like a Partner at Sequoia. Be concise, punchy, and data-driven.
    Do NOT use buzzwords. Use concrete facts and logic.`;

    const response = await callGeminiAPI(
        model,
        userPrompt,
        {
            systemInstruction: `You are a legendary VC pitch deck consultant.
            Generate a 12-slide Sequoia Capital style pitch deck.
            
            Structure:
            1. Company Purpose: A single declarative sentence. "AirBnB for X".
            2. Problem: The pain. Relatable and acute.
            3. Solution: The painkiller. How you solve it.
            4. Why Now: Trends, tech shifts, or cultural moments enabling this.
            5. Market Potential: TAM/SAM/SOM. Huge numbers.
            6. Competition: The matrix. Why you win.
            7. Product Overview: The magic. 
            8. Business Model: Unit economics and how you make money.
            9. Team: Why this team?
            10. Financials: Projections (Simulated).
            11. Vision: The "If we win" future state.
            12. Call to Action: The ask.

            Format lists with bullet points on new lines. Do not use Markdown symbols like ** or #.`,
            responseMimeType: "application/json",
            responseSchema: {
                type: Type.OBJECT,
                properties: {
                    companyPurpose: { type: Type.STRING, description: "Slide 1: Single declarative sentence. 'AirBnB for X'." },
                    problem: { type: Type.STRING, description: "Slide 2: The pain point." },
                    solution: { type: Type.STRING, description: "Slide 3: The solution." },
                    whyNow: { type: Type.STRING, description: "Slide 4: Why is now the right time?" },
                    marketPotential: { type: Type.STRING, description: "Slide 5: TAM/SAM/SOM." },
                    competition: { type: Type.STRING, description: "Slide 6: Competitive landscape." },
                    productOverview: { type: Type.STRING, description: "Slide 7: Product magic." },
                    businessModel: { type: Type.STRING, description: "Slide 8: How we make money." },
                    team: { type: Type.STRING, description: "Slide 9: Founding team." },
                    financials: { type: Type.STRING, description: "Slide 10: Projected revenue." },
                    vision: { type: Type.STRING, description: "Slide 11: The 10-year vision." },
                    callToAction: { type: Type.STRING, description: "Slide 12: The investment ask." }
                },
                required: ["companyPurpose", "problem", "solution", "whyNow", "marketPotential", "competition", "productOverview", "businessModel", "team", "financials", "vision", "callToAction"]
            }
        }
    );
    return JSON.parse(response.text);
};

// Image Generators (Restricted to PRO model)

export const generateLogo = async (title: string, description: string, instructions?: string): Promise<string> => {
    const prompt = `A modern, minimalist app logo for "${title}". 
    Context: ${description}. 
    Style: Vector art, flat design, high contrast, app icon style.
    ${instructions ? `Specific Instructions: ${instructions}` : ''}`;
    
    const response = await callGeminiAPI(
        MODEL_IMAGE_PRO, 
        [{ role: 'user', parts: [{ text: prompt }] }],
        { imageConfig: { aspectRatio: '1:1' } }
    );
    
    const raw = response.raw;
    const base64 = raw.candidates?.[0]?.content?.parts?.find((p: any) => p.inlineData)?.inlineData?.data;
    
    if (base64) return `data:image/png;base64,${base64}`;
    throw new Error("No image generated");
};

export const generateLandingPage = async (title: string, description: string, instructions?: string): Promise<string> => {
    const prompt = `High quality website landing page design for "${title}".
    Context: ${description}.
    Style: Linear.app style, dark mode, glowing gradients, modern UI, sleek, bento grid layout.
    ${instructions ? `Specific Instructions: ${instructions}` : ''}`;

    const response = await callGeminiAPI(
        MODEL_IMAGE_PRO,
        [{ role: 'user', parts: [{ text: prompt }] }],
        { imageConfig: { aspectRatio: '16:9', imageSize: '1K' } }
    );
    
    const raw = response.raw;
    const base64 = raw.candidates?.[0]?.content?.parts?.find((p: any) => p.inlineData)?.inlineData?.data;
    if (base64) return `data:image/png;base64,${base64}`;
    throw new Error("No image generated");
};

export const generateProductConcept = async (title: string, description: string, productForm: string, instructions?: string): Promise<string> => {
    const isApp = productForm.toLowerCase().includes('app') || productForm.toLowerCase().includes('saas') || productForm.toLowerCase().includes('software');
    
    const prompt = isApp 
        ? `A photorealistic iPhone 15 mockup showing the interface of the app "${title}".
           Context: ${description}.
           Screen UI: Modern, clean, dark mode, functional.
           ${instructions ? `Instructions: ${instructions}` : ''}`
        : `A photorealistic product photography shot of "${title}" (${productForm}).
           Context: ${description}.
           Lighting: Studio lighting, cinematic.
           ${instructions ? `Instructions: ${instructions}` : ''}`;

    const response = await callGeminiAPI(
        MODEL_IMAGE_PRO,
        [{ role: 'user', parts: [{ text: prompt }] }],
        { imageConfig: { aspectRatio: '4:3', imageSize: '1K' } }
    );
    
    const raw = response.raw;
    const base64 = raw.candidates?.[0]?.content?.parts?.find((p: any) => p.inlineData)?.inlineData?.data;
    if (base64) return `data:image/png;base64,${base64}`;
    throw new Error("No image generated");
};
